import React, { useContext } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import Barcode from 'react-barcode';
import MonDemoQLD from '../../../assets/img/mdemob.jpg';
import { UserContext } from '../../../contexts/UserContext';
import { DatabaseContext } from '../../../contexts/DatabaseContext';
import { PatronContext } from '../../../contexts/PatronContext';
import { PaneContext } from '../../../contexts/PaneContext';

moment.locale('en-AU');

const CardBack = () => {
  const { previewPanePhoto } = useContext(PaneContext);
  const { apiUrl } = useContext(DatabaseContext);
  const { patron } = useContext(PatronContext);
  const { Site } = useContext(UserContext);
  const {
    siteType,
    barcodePrefix,
    barcodeSuffix,
    barcodeEnabled,
    printedBarcode,
    printedID,
    showDetails,
    showPhoto,
    logoEnabled,
  } = Site;

  const placeholderImage =
    'https://office.monitorwa.com.au/idcaptureapi/defaults/patron.jpg';

  const onImageError = (e) => {
    e.target.src = placeholderImage;
  };

  return (
    <Container
      style={{
        backgroundImage: `url(${Site.siteType === 4 ? MonDemoQLD : ''})`,
        backgroundSize: 'cover',
      }}
    >
      <Content>
        <SiteDetails>
          {showDetails && (
            <>
              <SiteName>{Site.name ?? ''}</SiteName>
              <SiteDetail>
                {`${Site.street ?? ''}, ${Site.suburb ?? ''}, ${
                  Site.state ?? ''
                } ${Site.postcode ?? ''}`}
              </SiteDetail>
              <SiteDetail>{Site.telephone ?? ''}</SiteDetail>
            </>
          )}
        </SiteDetails>

        {showPhoto && previewPanePhoto && (
          <PatronPhoto>
            <img
              src={previewPanePhoto ? previewPanePhoto : placeholderImage}
              alt={patron.externalID}
              onError={onImageError}
            />
          </PatronPhoto>
        )}

        <PatronDetails>
          <PatronDetail style={{ width: '100%' }}>
            <Label>Name</Label>
            <Detail>{`${patron.firstName} ${patron.lastName}`}</Detail>
          </PatronDetail>
          {siteType === 0 || siteType === 5 ? (
            <PatronDetail>
              <Label className='label'>DOB</Label>
              <Detail>{moment(patron.dateOfBirth).format('DD-MM-YYYY')}</Detail>
            </PatronDetail>
          ) : (
            <></>
          )}
          <PatronDetail>
            <Label>
              {siteType === 0 || siteType === 5 ? 'Student' : 'Patron'} ID
            </Label>
            <Detail>
              {printedID === 'externalID'
                ? patron.externalID
                : printedID === 'curriculum'
                ? patron.curriculum
                : printedID === 'custom1'
                ? patron.custom1
                : printedID === 'custom2'
                ? patron.custom2
                : printedID === 'custom3'
                ? patron.custom3
                : ''}
            </Detail>
          </PatronDetail>
          {Site.custom1Enabled && (
            <PatronDetail>
              <Label>{Site.custom1}</Label>
              <Detail>{`${patron.custom1}`}</Detail>
            </PatronDetail>
          )}{' '}
          {Site.custom2Enabled && (
            <PatronDetail>
              <Label>{Site.custom2}</Label>
              <Detail>{`${patron.custom2}`}</Detail>
            </PatronDetail>
          )}{' '}
          {Site.custom3Enabled && (
            <PatronDetail>
              <Label>{Site.custom3}</Label>
              <Detail>{`${patron.custom3}`}</Detail>
            </PatronDetail>
          )}
        </PatronDetails>

        {logoEnabled && (
          <SiteLogo>
            <img
              src={`${apiUrl}/logos/${Site.siteCode}.png`}
              alt={Site.name ?? 'siteLogo'}
            />
          </SiteLogo>
        )}

        <BarcodeContainer>
          {barcodeEnabled && (
            <Barcode
              value={`${barcodePrefix}${
                printedBarcode === 'externalID'
                  ? patron.externalID
                  : printedBarcode === 'curriculum'
                  ? patron.curriculum
                  : printedBarcode === 'custom1'
                  ? patron.custom1
                  : printedBarcode === 'custom2'
                  ? patron.custom2
                  : printedBarcode === 'custom3'
                  ? patron.custom3
                  : patron.curriculum
              }${barcodeSuffix}`}
              format='CODE128'
              background='none'
              lineColor='#000'
              height={20}
              width={1}
              marginLeft={1}
              marginTop={1}
              marginBottom={1}
              marginRight={1}
              fontSize={10}
            />
          )}
        </BarcodeContainer>
      </Content>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  background: #ffffff;
  color: #000000;
  border-radius: 0.8rem;
  padding-top: 62%;
  font-family: Arial, Helvetica, sans-serif;
  overflow: hidden;
`;

const Content = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 2.5fr 3fr 3fr 80px;
  align-items: center;
`;

const SiteDetails = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  font-size: calc(0.8rem + 0.3vw);
  line-height: 1;
  padding-top: 0.5rem;
`;
const SiteName = styled.div`
  font-size: calc(0.8rem + 0.4vw);
  font-weight: 800;
  text-transform: uppercase;
`;
const SiteDetail = styled.div`
  font-size: calc(0.8rem + 0.3vw);
`;

const PatronPhoto = styled.div`
  padding: 0 1rem;
  img {
    max-width: 100%;
  }
`;

const PatronDetails = styled.div`
  grid-column: 2 / 4;
  display: flex;
  flex-wrap: wrap;
  margin-top: -1rem;
`;
const PatronDetail = styled.div`
  line-height: 1;
  margin-right: 2rem;
  margin-bottom: 0.8rem;
`;
const Label = styled.span`
  font-weight: 700;
  font-style: italic;
  font-size: calc(0.8rem + 0.3vw);
  margin-bottom: 0.5rem;
`;
const Detail = styled.div`
  font-size: calc(0.8rem + 0.3vw);
`;

const SiteLogo = styled.div`
  padding: 0 1rem;
  img {
    max-width: 100%;
  }
`;

const BarcodeContainer = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  padding-bottom: 0.5rem;
`;

export default CardBack;
